import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageTitle } from "@/components/app/ui";
import { useSiteContent, fileUrl } from "@/context/SiteContentContext";
import { HERO_SLIDES, FAQS } from "@/data/content";
import { api, errMsg } from "@/lib/api";

const ListEditor = ({ items, setItems, fields, blank, testId }) => (
  <div className="space-y-4">
    {items.map((it, i) => (
      <div key={i} className="card-box p-5 grid md:grid-cols-2 gap-3 relative" data-testid={`${testId}-item-${i}`}>
        {fields.map(([k, l, area]) => (
          <div key={k} className={`space-y-1.5 ${area ? "md:col-span-2" : ""}`}><Label className="text-xs">{l}</Label>
            {area ? <Textarea rows={2} value={it[k] || ""} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} data-testid={`${testId}-${k}-${i}`} />
              : <Input value={it[k] || ""} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} data-testid={`${testId}-${k}-${i}`} />}
          </div>
        ))}
        <button onClick={() => setItems(items.filter((_, j) => j !== i))} className="absolute top-3 right-3 text-muted-foreground hover:text-red-500" data-testid={`${testId}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button onClick={() => setItems([...items, blank])} className="btn-ghost !py-2 text-xs" data-testid={`${testId}-add`}><Plus className="h-3.5 w-3.5" /> Add</button>
  </div>
);

const Clients = () => {
  const { content, reload } = useSiteContent();
  const [f, setF] = useState({ name: "", website: "", file: null });
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    const fd = new FormData();
    fd.append("file", f.file); fd.append("name", f.name); fd.append("website", f.website);
    setBusy(true);
    try { await api.post("/admin/site/clients", fd); toast.success("Logo added"); setF({ name: "", website: "", file: null }); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/admin/site/clients/${id}`); reload(); };
  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">The "Our clients" two-row scrolling section on Home and About stays hidden until at least one logo is added.</p>
      <div className="card-box p-5 grid md:grid-cols-4 gap-3 items-end">
        <div className="space-y-1.5"><Label className="text-xs">Client name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cms-client-name" /></div>
        <div className="space-y-1.5"><Label className="text-xs">Website (optional)</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} data-testid="cms-client-website" /></div>
        <label className="btn-ghost !py-2 text-xs cursor-pointer"><Upload className="h-3.5 w-3.5" />{f.file ? f.file.name.slice(0, 18) : "Choose logo"}<input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={(e) => setF({ ...f, file: e.target.files?.[0] })} data-testid="cms-client-file" /></label>
        <button disabled={!f.name || !f.file || busy} onClick={upload} className="btn-brand !py-2 disabled:opacity-50" data-testid="cms-client-upload">{busy ? "Uploading..." : "Upload logo"}</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {(content?.clients || []).map((c) => (
          <div key={c.id} className="card-box p-3 relative" data-testid={`cms-client-${c.id}`}>
            <div className="h-16 rounded-lg bg-white flex items-center justify-center p-2"><img src={fileUrl(c.logo_url)} alt={c.name} className="max-h-12 object-contain" /></div>
            <div className="mt-2 text-xs truncate">{c.name}</div>
            <button onClick={() => del(c.id)} className="absolute top-2 right-2 text-red-500" data-testid={`cms-client-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function AdminCMS() {
  const { content, reload } = useSiteContent();
  const [ann, setAnn] = useState({ enabled: false, text: "", link: "" });
  const [slides, setSlides] = useState([]);
  const [faqs, setFaqs] = useState([]);
  useEffect(() => {
    if (!content) return;
    setAnn(content.announcement || { enabled: false, text: "", link: "" });
    setSlides(content.hero_slides?.length ? content.hero_slides : HERO_SLIDES.map(({ eyebrow, title, accent, copy, img }) => ({ eyebrow, title, accent, copy, img })));
    setFaqs(content.faqs?.length ? content.faqs : FAQS);
  }, [content]);
  const save = async () => {
    try { await api.put("/admin/site/content", { announcement: ann, hero_slides: slides, faqs }); toast.success("Website updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-cms-page">
      <PageTitle title="Website CMS" copy="Manage homepage banners, the announcement bar, FAQs and client logos. Changes go live immediately.">
        <button onClick={save} className="btn-brand" data-testid="cms-save-btn">Publish changes</button>
      </PageTitle>
      <Tabs defaultValue="banner">
        <TabsList className="rounded-full"><TabsTrigger value="banner" className="rounded-full" data-testid="cms-tab-banner">Announcement</TabsTrigger><TabsTrigger value="hero" className="rounded-full" data-testid="cms-tab-hero">Hero carousel</TabsTrigger>
          <TabsTrigger value="faqs" className="rounded-full" data-testid="cms-tab-faqs">FAQs</TabsTrigger><TabsTrigger value="clients" className="rounded-full" data-testid="cms-tab-clients">Client logos</TabsTrigger></TabsList>
        <TabsContent value="banner" className="mt-6">
          <div className="card-box p-6 space-y-4 max-w-2xl">
            <label className="flex items-center gap-3 text-sm"><Switch checked={ann.enabled} onCheckedChange={(v) => setAnn({ ...ann, enabled: v })} data-testid="cms-ann-switch" /> Show announcement bar on the website</label>
            <div className="space-y-1.5"><Label>Text</Label><Input value={ann.text} onChange={(e) => setAnn({ ...ann, text: e.target.value })} data-testid="cms-ann-text" /></div>
            <div className="space-y-1.5"><Label>Link (optional)</Label><Input value={ann.link} onChange={(e) => setAnn({ ...ann, link: e.target.value })} data-testid="cms-ann-link" /></div>
          </div>
        </TabsContent>
        <TabsContent value="hero" className="mt-6"><ListEditor items={slides} setItems={setSlides} testId="cms-slide" blank={{ eyebrow: "", title: "", accent: "", copy: "", img: "" }}
          fields={[["eyebrow", "Eyebrow"], ["title", "Title"], ["accent", "Gradient accent line"], ["img", "Background image URL"], ["copy", "Description", true]]} /></TabsContent>
        <TabsContent value="faqs" className="mt-6"><ListEditor items={faqs} setItems={setFaqs} testId="cms-faq" blank={{ q: "", a: "" }} fields={[["q", "Question", true], ["a", "Answer", true]]} /></TabsContent>
        <TabsContent value="clients" className="mt-6"><Clients /></TabsContent>
      </Tabs>
    </div>
  );
}
